import { Search } from 'lucide-react';
import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { autoFormatPostcode, normalisePostcode } from '../lib/postcode';
import { tx } from '../i18n';
import Spinner from './Spinner';

interface PostcodeFormProps {
  onSubmit: (postcode: string) => void;
  loading?: boolean;
  initialValue?: string;
}

/**
 * The single input the product is built around.
 *
 * <p>Formats as the tenant types and validates on submit rather than on every keystroke, so it
 * does not shout at somebody who is half way through typing.
 */
export default function PostcodeForm({
  onSubmit,
  loading = false,
  initialValue = '',
}: PostcodeFormProps) {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const [value, setValue] = useState(autoFormatPostcode(initialValue));
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalised = normalisePostcode(value);
    if (!normalised) {
      setError(tx('That does not look like a UK postcode. Try something like E1 6AN.', 'Bu bir Birleşik Krallık posta kodu gibi görünmüyor. E1 6AN gibi bir şey deneyin.',
    'To nie wygląda na brytyjski kod pocztowy. Spróbuj czegoś w rodzaju E1 6AN.',
    'Isto não parece um código postal britânico. Experimente algo como E1 6AN.',
    'Esto no parece un código postal británico. Prueba con algo como E1 6AN.'));
      return;
    }
    setError(null);
    onSubmit(normalised);
  };

  return (
    <form onSubmit={handleSubmit} noValidate aria-busy={loading}>
      {/*
        * A visible label, not a screen-reader-only one. "E1 6AN" in the box is an example and
        * disappears the moment anything is typed, which left a reader who started typing a street
        * name with nothing on screen saying the field wants a postcode.
        */}
      <label htmlFor={inputId} className="field-label">
        {tx(
          'Enter an English postcode',
          'Bir İngiltere posta kodu girin',
          'Wpisz angielski kod pocztowy',
          'Introduza um código postal inglês',
          'Introduce un código postal inglés',
        )}
      </label>
      <div className="input-row">
        <input
          id={inputId}
          name="postcode"
          type="text"
          inputMode="text"
          autoComplete="postal-code"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={8}
          placeholder={tx('e.g. E1 6AN', 'örn. E1 6AN', 'np. E1 6AN', 'ex. E1 6AN', 'p. ej. E1 6AN')}
          disabled={loading}
          className="input input--postcode"
          value={value}
          aria-invalid={error !== null}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            setValue(autoFormatPostcode(event.target.value));
            if (error) {
              setError(null);
            }
          }}
        />
        <button type="submit" className="button" disabled={loading}>
          {loading ? (
            <Spinner label={tx('Finding addresses', 'Adresler bulunuyor',
    'Szukanie adresów',
    'A procurar moradas',
    'Buscando direcciones')} size={16} />
          ) : (
            <>
              <Search size={16} aria-hidden="true" />
              {tx('Find the address', 'Adresi bul',
    'Znajdź adres',
    'Encontrar a morada',
    'Buscar la dirección')}
            </>
          )}
        </button>
      </div>

      {error ? (
        <p id={errorId} role="alert" className="small field-error">
          {error}
        </p>
      ) : (
        <p className="small muted" style={{ marginTop: 8 }}>
          {tx(
            'A postcode is all you need. Select any address to instantly inspect the property through official UK open data.',
            'Tek gereken bir posta kodu. Resmî Birleşik Krallık açık verisiyle konutu anında incelemek için bir adres seçin.',
    'Wystarczy kod pocztowy. Wybierz adres, aby od razu sprawdzić lokal na podstawie oficjalnych brytyjskich danych otwartych.',
    'Basta um código postal. Escolha uma morada para analisar o imóvel de imediato com dados abertos oficiais do Reino Unido.',
    'Solo necesitas un código postal. Elige una dirección para analizar la vivienda al instante con datos abiertos oficiales del Reino Unido.',
          )}
        </p>
      )}
    </form>
  );
}
